import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Play, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const JobStatus = ({ s }) => {
  const cls = s === 'succeeded' ? 'text-emerald-400' : s === 'failed' ? 'text-red-400' : s === 'running' ? 'text-sky-400' : 'text-amber-400';
  const Icon = s === 'succeeded' ? CheckCircle2 : s === 'failed' ? XCircle : AlertTriangle;
  return <span className={`inline-flex items-center gap-1 text-xs font-semibold ${cls}`} data-testid="job-status"><Icon size={12} /> {s.replace(/_/g, ' ')}</span>;
};

const AdminPickOutcomes = () => {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const [j, s] = await Promise.all([
        axios.get(`${API}/api/recommendations/outcomes/jobs?limit=20`, { withCredentials: true }),
        axios.get(`${API}/api/recommendations/outcomes/stats`),
      ]);
      setData(j.data); setStats(s.data); setError(null);
    } catch (err) {
      setError(err?.response?.status === 403 ? 'Administrator access required.' : 'Could not load job history.');
    }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/api/recommendations/outcomes/evaluate`, {}, { withCredentials: true, timeout: 180000 });
      toast.success(`Job ${r.data.status.replace(/_/g, ' ')}: ${r.data.observations_added ?? 0} observations added`);
      await load();
    } catch (_) {
      toast.error('Evaluation run failed');
    } finally { setRunning(false); }
  };

  if (error) return <p className="text-sm text-red-400" data-testid="admin-outcomes-error">{error}</p>;
  if (!data) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-5" data-testid="admin-pick-outcomes">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-xs text-slate-400">
          <p>Schedule: {data.schedule} · cron secret {data.cron_secret_configured ? <span className="text-emerald-400">configured</span> : <span className="text-red-400">MISSING</span>}</p>
          <p>Records: {Object.entries(data.record_status_counts).map(([k, v]) => `${v} ${k}`).join(' · ')}</p>
        </div>
        <Button size="sm" onClick={runNow} disabled={running} className="ml-auto" data-testid="outcomes-run-now"><Play className={`h-4 w-4 mr-2 ${running ? 'animate-pulse' : ''}`} /> {running ? 'Running…' : 'Run evaluation now'}</Button>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-outcomes-stats">
          {Object.values(stats.checkpoints).map((cp) => (
            <div key={cp.checkpoint_trading_days} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3 text-xs text-slate-300">
              <p className="font-semibold text-white mb-1">+{cp.checkpoint_trading_days} trading days</p>
              {cp.available ? <p>{cp.win_rate_pct}% above reference ({cp.wins}/{cp.losses}) · median {cp.median_change_pct}%</p> : <p className="italic text-slate-400">{cp.message}</p>}
              <p className="text-slate-500 mt-1">{cp.evaluated} evaluated · {cp.pending} pending · {cp.unavailable} unavailable · {cp.excluded_invalid} excluded · {cp.superseded_invalidated} invalidated</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl">
        <div className="px-4 py-3 border-b border-slate-700/50 text-sm font-semibold text-white">Job runs</div>
        {data.jobs.length === 0 ? <p className="p-6 text-sm text-slate-500 text-center" data-testid="jobs-empty">No runs recorded yet. The daily cron or "Run evaluation now" will create the first entry.</p> : (
          <div className="divide-y divide-slate-700/30">
            {data.jobs.map((j) => (
              <div key={j.run_id} className="px-4 py-3 text-xs" data-testid="job-row">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <JobStatus s={j.status} />
                  <span className="text-slate-300">{fmt(j.started_at)}</span>
                  <span className="text-slate-500">{j.trigger}{j.actor ? ` · ${j.actor}` : ''} · {j.duration_seconds ?? '…'}s</span>
                  <span className="text-slate-500 font-mono truncate max-w-[220px]">{j.run_id}</span>
                </div>
                {j.status !== 'running' && (
                  <p className="text-slate-400 mt-1">
                    {j.observations_added ?? 0} observations added · {j.unavailable_marked ?? 0} marked unavailable · {j.symbols_checked ?? 0}/{j.symbols_due ?? 0} symbols · {j.backfilled ?? 0} back-filled
                    {j.records_with_invalidated_observations > 0 && ` · ${j.records_with_invalidated_observations} records had observations invalidated`}
                    {j.history_window_skipped > 0 && ` · ${j.history_window_skipped} skipped (history window)`}
                  </p>
                )}
                {j.error && <p className="text-red-400 mt-1" data-testid="job-error">{j.error}</p>}
                {j.provider_failures?.length > 0 && <p className="text-amber-400 mt-1" data-testid="job-provider-failures">Provider failures: {j.provider_failures.map((f) => `${f.symbol} (${f.reason})`).join(', ')}</p>}
                {j.stale_symbols?.length > 0 && <p className="text-amber-400 mt-1">Stale data skipped: {j.stale_symbols.map((f) => `${f.symbol} (newest ${f.newest_bar})`).join(', ')}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPickOutcomes;
